// client/src/components/pages/LexiStar/Replay/AssetMultiPicker.tsx
//
// ST-00277 — the trucks to replay: one, several, or all of them. A field that opens a panel with
// a search box, "Select all" / "Deselect all" (over the trucks the search shows), the count and
// the limit, and one checkbox per truck. At most `max` trucks (MAX_REPLAY_TRUCKS, 100 since
// ST-00311): "Select all" takes the first `max` and says so; once `max` are chosen the others are
// disabled with a note that says why. Past 25 trucks the panel says how long a time range so many
// trucks cover (./replayLimits). Esc, "Done" or a click outside closes it (focus returns to the
// field). Theme tokens only (bg-surface, slate-*, primary-*): light and dark.
//
// ST-00312: the field is as tall as the form's other fields: 38 px with a mouse (the shared
// pickers' desktop trigger, the Replay button), 44 px on a phone or a touch screen (.touch-target,
// the breakpoint at which the shared pickers open as a bottom sheet).

import { type KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaSearch } from 'react-icons/fa';
import {
  FULL_RANGE_TRUCKS,
  MAX_REPLAY_TRUCKS,
  deselectWithin,
  maxMultiRangeHours,
  orderLike,
  rangeWords,
  selectAllWithin,
  toggleKey,
  trucksWord,
} from './replayMulti';

export interface AssetPickerOption {
  /** The asset key (reg:…, asset:…). */
  value: string;
  /** The registration. */
  label: string;
  /** The fleet number, or the kind. */
  sublabel?: string | null;
}

export interface AssetMultiPickerProps {
  id?: string;
  options: AssetPickerOption[];
  value: string[];
  onChange: (keys: string[]) => void;
  max?: number;
  loading?: boolean;
  /** The list was cut by the asset list's page size: search to find the others. */
  more?: boolean;
  /** Search beyond the loaded list (the asset register's own search). */
  onSearch?: (q: string) => void;
}

const matches = (o: AssetPickerOption, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase().replace(/\s+/g, '');
  return [o.label, o.sublabel || '', o.value].some((s) => s.toLowerCase().replace(/\s+/g, '').includes(needle));
};

const smallButton =
  'px-2.5 py-1 rounded-md text-xs font-medium border border-slate-300 bg-surface text-slate-700 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

export default function AssetMultiPicker({
  id,
  options,
  value,
  onChange,
  max = MAX_REPLAY_TRUCKS,
  loading = false,
  more = false,
  onSearch,
}: AssetMultiPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const panelId = useId();
  const countId = useId();

  const order = useMemo(() => options.map((o) => o.value), [options]);
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const shown = useMemo(() => options.filter((o) => matches(o, query.trim())), [options, query]);
  const shownKeys = useMemo(() => shown.map((o) => o.value), [shown]);
  const atLimit = value.length >= max;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setNote(null);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close]);

  // The asset register's own search when the loaded list is cut (debounced).
  useEffect(() => {
    if (!onSearch || !more) return undefined;
    const t = setTimeout(() => onSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, onSearch, more]);

  const emit = (keys: string[]) => onChange(orderLike(keys, order));

  const selectAll = () => {
    const { keys, capped } = selectAllWithin(value, shownKeys, max);
    emit(keys);
    setNote(
      capped
        ? `${max} trucks is the most one replay shows: the first ${max} are selected.`
        : null
    );
  };
  const deselectAll = () => {
    emit(query.trim() ? deselectWithin(value, shownKeys) : []);
    setNote(null);
  };
  const toggle = (key: string) => {
    const { keys, refused } = toggleKey(value, key, max);
    emit(keys);
    setNote(refused ? `${max} trucks is the most one replay shows. Deselect one to add another.` : null);
  };

  const onPanelKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  };

  const labels = value.map((k) => byValue.get(k)?.label || k.replace(/^(reg|asset|device|ext):/, ''));
  const summary =
    value.length === 0
      ? null
      : value.length === 1
        ? labels[0]
        : `${trucksWord(value.length)}: ${labels.slice(0, 3).join(', ')}${value.length > 3 ? ', …' : ''}`;
  const allShownSelected = shownKeys.length > 0 && shownKeys.every((k) => value.includes(k));
  const noneShownSelected = !shownKeys.some((k) => value.includes(k));

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        onClick={() => (open ? close(false) : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`Trucks: ${summary || 'none chosen'}`}
        data-testid="replay-asset-picker"
        className="touch-target w-full min-h-[38px] flex items-center gap-2 px-2.5 py-1.5 text-left text-sm rounded-[4px] border border-slate-300 bg-surface text-slate-900 hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span className={`flex-1 min-w-0 truncate ${summary ? '' : 'text-slate-500'}`}>
          {summary || (loading ? 'Loading trucks…' : 'Choose trucks…')}
        </span>
        {value.length > 1 && (
          <span className="flex-shrink-0 px-1.5 rounded-full bg-primary-50 text-primary-700 text-[11px] font-semibold tabular-nums">
            {value.length}
          </span>
        )}
        <FaChevronDown size={11} className="flex-shrink-0 text-slate-500" aria-hidden="true" />
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Choose the trucks to replay"
          aria-describedby={countId}
          onKeyDown={onPanelKey}
          className="absolute left-0 top-full mt-1 z-[1100] w-full min-w-[18rem] sm:w-[24rem] rounded-lg border border-slate-200 bg-surface shadow-xl"
        >
          <div className="p-2.5 border-b border-slate-200 space-y-2">
            <label className="relative block">
              <span className="sr-only">Search trucks</span>
              <FaSearch size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search registration or fleet no."
                className="w-full pl-7 pr-2 py-1.5 text-sm rounded-md border border-slate-300 bg-surface text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <span id={countId} className="text-xs text-slate-600 tabular-nums mr-auto" data-testid="replay-picker-count">
                {value.length} of {options.length} selected
              </span>
              <button type="button" onClick={selectAll} disabled={allShownSelected} className={smallButton}>
                {query.trim() ? 'Select shown' : 'Select all'}
              </button>
              <button type="button" onClick={deselectAll} disabled={noneShownSelected} className={smallButton}>
                {query.trim() ? 'Deselect shown' : 'Deselect all'}
              </button>
            </div>
            {note && (
              <p role="status" className="text-xs font-medium text-amber-700" data-testid="replay-picker-note">
                {note}
              </p>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1" aria-label="Trucks">
            {shown.length === 0 && (
              <li className="px-3 py-3 text-sm text-slate-500">
                {loading ? 'Loading trucks…' : query.trim() ? 'No truck matches.' : 'No trucks to replay.'}
              </li>
            )}
            {shown.map((o) => {
              const checked = value.includes(o.value);
              const disabled = !checked && atLimit;
              return (
                <li key={o.value}>
                  <label
                    className={`flex items-center gap-2.5 px-3 py-1.5 text-sm ${
                      disabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-800 cursor-pointer hover:bg-slate-50'
                    }`}
                    title={disabled ? `${max} trucks is the most one replay shows` : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(o.value)}
                      className="w-4 h-4 flex-shrink-0 rounded"
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <span className="flex-1 min-w-0 truncate font-medium">{o.label}</span>
                    {o.sublabel && <span className="flex-shrink-0 text-xs text-slate-500 truncate max-w-[40%]">{o.sublabel}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between gap-2 p-2.5 border-t border-slate-200">
            <span className="text-[11px] text-slate-500" data-testid="replay-picker-limit">
              {more
                ? `Not every truck is listed: search to find the others. Up to ${max} in one replay.`
                : `Up to ${max} trucks in one replay, all on one clock.`}
              {value.length > FULL_RANGE_TRUCKS && (
                <span className="block" data-testid="replay-picker-range">
                  {`${trucksWord(value.length)} cover a time range of up to ${rangeWords(maxMultiRangeHours(value.length))}.`}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => close(true)}
              className="px-3 py-1 rounded-md text-xs font-semibold text-surface bg-primary-700 hover:bg-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
